import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/Table";
import { ChartBar, FilePdf } from "@phosphor-icons/react";

const COLORS = { moving: "#34C759", idle: "#5AC8FA", stopped: "#FF9500", offline: "#8E8E93", panic: "#FF3B30",
  critical: "#FF3B30", warning: "#FF9500", info: "#5AC8FA" };
const pick = (n) => COLORS[n] || "#0091e6";

const Card = ({ title, children }) => (
  <div className="bg-card border border-border rounded-sm p-4">
    <div className="label-caps mb-3">{title}</div>
    <div style={{ width: "100%", height: 260 }}>{children}</div>
  </div>
);

export default function Graphics() {
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/graphics").then((r) => setD(r.data)).catch(() => toast.error("Failed to load")); }, []);

  const axis = { fontSize: 11, fill: "#9fb0c3" };
  const tip = { background: "#0d2038", border: "1px solid #1c3a5c", borderRadius: 4, fontSize: 12 };

  return (
    <div className="p-5 space-y-4">
      <PageHeader title="Graphics" subtitle={`Fleet analytics${d ? ` · ${d.vehicle_count} vehicles` : ""} · today`}
        actions={<button data-testid="graphics-pdf" onClick={() => window.print()}
          className="flex items-center gap-1.5 border border-border text-sm font-semibold px-3 py-2 rounded-sm hover:border-primary"><FilePdf size={15} weight="bold" /> Export PDF</button>} />

      {!d ? <div className="text-muted-foreground text-sm">Loading charts…</div> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="graphics-grid">
          <Card title="Top 10 by Distance (km)">
            <ResponsiveContainer>
              <BarChart data={d.distance}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1c3a5c" />
                <XAxis dataKey="name" tick={axis} angle={-25} textAnchor="end" height={50} />
                <YAxis tick={axis} />
                <Tooltip contentStyle={tip} />
                <Bar dataKey="distance" fill="#0091e6" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Vehicle Status Distribution">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={d.status} dataKey="value" nameKey="name" outerRadius={90} label>
                  {d.status.map((s) => <Cell key={s.name} fill={pick(s.name)} />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Tooltip contentStyle={tip} />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Duration — Idle vs Engine Hours">
            <ResponsiveContainer>
              <BarChart data={d.duration}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1c3a5c" />
                <XAxis dataKey="name" tick={axis} angle={-25} textAnchor="end" height={50} />
                <YAxis tick={axis} />
                <Tooltip contentStyle={tip} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="engine_hrs" fill="#34C759" radius={[3, 3, 0, 0]} />
                <Bar dataKey="idle_hrs" fill="#FF9500" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Alerts by Severity">
            <ResponsiveContainer>
              <BarChart data={d.alerts}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1c3a5c" />
                <XAxis dataKey="name" tick={axis} />
                <YAxis tick={axis} />
                <Tooltip contentStyle={tip} />
                <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                  {d.alerts.map((a) => <Cell key={a.name} fill={pick(a.name)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}
      {d && !d.vehicle_count && <p className="text-sm text-muted-foreground">No vehicles yet — charts will populate once devices are active.</p>}
    </div>
  );
}
